import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, Check, Gift } from 'lucide-react';
import useTrackingData, { getTrackingData } from '../hooks/useTrackingData';
import { formatPhoneNumber, validatePhoneNumber, normalizePhoneNumber, normalizeEmail, validateEmail } from '../utils/validation';

interface RSVPPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

const RSVPPopup: React.FC<RSVPPopupProps> = ({ isOpen, onClose }) => {
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ name?: string; phone?: string; email?: string }>({});

  useTrackingData();

  useEffect(() => {
    if (isOpen) {
      setName('');
      setPhoneNumber('');
      setEmail('');
      setIsSubmitted(false);
      setErrors({});
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);


  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhoneNumber(formatted);
    setErrors((prev) => ({ ...prev, phone: undefined }));
  };

  const handleSubmit = async () => {
    const newErrors: { name?: string; phone?: string; email?: string } = {};

    if (!name.trim()) {
      newErrors.name = 'Please enter your name';
    }
    if (!validatePhoneNumber(phoneNumber)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    if (!validateEmail(email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);

    try {
      // Normalize phone and email before sending
      const normalizedPhone = normalizePhoneNumber(phoneNumber);
      const normalizedEmail = normalizeEmail(email);

      // Validate again with normalized values
      if (!normalizedPhone) {
        setErrors({ phone: 'Please enter a valid phone number' });
        setIsLoading(false);
        return;
      }

      if (!validateEmail(normalizedEmail)) {
        setErrors({ email: 'Please enter a valid email address' });
        setIsLoading(false);
        return;
      }

      const trackingData = getTrackingData({
        name: name.trim(),
        phone: normalizedPhone,
        email: normalizedEmail,
        lead_type: 'daxxify_day_rsvp',
      });

      const response = await fetch('/api/rsvp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(trackingData),
      });

      if (!response.ok) {
        console.error('Error sending webhook:', response.statusText);
      } else {
        console.log('RSVP webhook sent successfully');

        if (typeof window.trackEvent === 'function') {
          window.trackEvent('daxxify_day_rsvp_submit', {
            element_type: 'form',
            submission_type: 'daxxify_day_rsvp',
            success: true,
          });
        }
      }
    } catch (error) {
      console.error('Error sending webhook:', error);
    }

    await new Promise((resolve) => setTimeout(resolve, 1000));
    setIsLoading(false);
    setIsSubmitted(true);
  };

  const handleBackgroundClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={handleBackgroundClick}
    >
      <div className="relative w-full max-w-md mx-auto bg-[#fefbf4]/90 backdrop-blur-md rounded-3xl border border-white/20 shadow-2xl max-h-[90vh] overflow-y-auto">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-11 h-11 bg-white/30 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/40 transition-all duration-300 touch-manipulation"
          data-section="rsvp"
          data-event="rsvp_close_popup_click"
        >
          <X className="w-6 h-6 text-[#4d3e3e]" />
        </button>

        <div className="p-6 pb-4">
          <div className="flex items-center gap-3 mb-2">
            <Calendar className="w-7 h-7 text-[#e2c1b6]" />
            <h2 className="text-2xl font-bold text-[#4d3e3e]">
              {isSubmitted ? "You're In!" : 'RSVP for Daxxify Day'}
            </h2>
          </div>
          <div className="flex items-center gap-4 text-[#4d3e3e] opacity-80">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4 text-[#e2c1b6]" />
              January 21st
            </span>
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4 text-[#e2c1b6]" />
              10am - 2pm
            </span>
          </div>
        </div>

        <div className="px-6 pb-4">
          <div className="bg-[#e2c1b6]/10 rounded-xl p-4 mb-4">
            <h3 className="font-bold text-[#4d3e3e] mb-2 flex items-center gap-2">
              <Gift className="w-5 h-5 text-[#e2c1b6]" />
              Event Exclusives:
            </h3>
            <ul className="space-y-1 text-sm text-[#4d3e3e]">
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-[#e2c1b6]" />
                Daxxify at $6.50/unit
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-[#e2c1b6]" />
                25% off ZO skincare products
              </li>
              <li className="flex items-center gap-2">
                <Check className="w-4 h-4 text-[#e2c1b6]" />
                Complimentary light bites
              </li>
            </ul>
          </div>
        </div>

        <div className="px-6 mb-6">
          {isSubmitted ? (
            <div className="bg-green-50/60 backdrop-blur-sm rounded-xl p-6 text-center border border-green-200">
              <Check className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <h3 className="text-xl font-bold text-[#4d3e3e] mb-2">You're All Set!</h3>
              <p className="text-[#4d3e3e] mb-4">
                We've reserved your spot for Daxxify Day on January 21st. Check your phone and email for confirmation
                details.
              </p>
              <p className="text-sm text-[#4d3e3e] opacity-80 mb-4">
                Remember: Treatment must be received on 1/21 between 10am-2pm to receive the special pricing.
              </p>
              <div className="flex justify-center">
                <button
                  onClick={onClose}
                  className="bg-white/60 hover:bg-white backdrop-blur-sm rounded-full px-6 py-3 text-[#4d3e3e] font-bold text-sm"
                >
                  Done
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-[#4d3e3e] mb-1">Name</label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      setErrors((prev) => ({ ...prev, name: undefined }));
                    }}
                    placeholder="Your name"
                    className={`w-full py-3 px-4 rounded-xl bg-white/60 border ${
                      errors.name ? 'border-red-300' : 'border-transparent'
                    } focus:outline-none focus:ring-2 focus:ring-[#e2c1b6] text-[#4d3e3e]`}
                    disabled={isLoading}
                  />
                  {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#4d3e3e] mb-1">Phone</label>
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    placeholder="(520) 555-1234"
                    className={`w-full py-3 px-4 rounded-xl bg-white/60 border ${
                      errors.phone ? 'border-red-300' : 'border-transparent'
                    } focus:outline-none focus:ring-2 focus:ring-[#e2c1b6] text-[#4d3e3e]`}
                    disabled={isLoading}
                  />
                  {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#4d3e3e] mb-1">Email</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setErrors((prev) => ({ ...prev, email: undefined }));
                    }}
                    placeholder="you@email.com"
                    className={`w-full py-3 px-4 rounded-xl bg-white/60 border ${
                      errors.email ? 'border-red-300' : 'border-transparent'
                    } focus:outline-none focus:ring-2 focus:ring-[#e2c1b6] text-[#4d3e3e]`}
                    disabled={isLoading}
                  />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                </div>
              </div>

              <button
                onClick={handleSubmit}
                disabled={isLoading}
                className={`w-full py-3 rounded-xl font-bold text-white flex items-center justify-center gap-2 transition-all duration-300 ${
                  isLoading ? 'bg-[#e2c1b6]/70' : 'bg-[#e2c1b6] hover:bg-[#d4b3a5]'
                }`}
                data-section="rsvp"
                data-event="rsvp_submit_click"
              >
                {isLoading ? (
                  <>
                    <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    Reserving Your Spot...
                  </>
                ) : (
                  <>
                    <Check className="w-5 h-5" />
                    RSVP Now
                  </>
                )}
              </button>

              <p className="text-xs text-[#4d3e3e] opacity-70 text-center mt-4">
                Treatment must be received on January 21st between 10am-2pm to qualify for special pricing.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default RSVPPopup;
